// [LIVE-VENTES] Bloc activité en direct pour le dashboard owner
import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth, API } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Activity, TrendingUp, User, Clock, ShoppingBag } from "lucide-react";

// [LIVE-VENTES] Avatar vendeur
function VendeurAvatar({ prenom, role, size = 32, accentColor }) {
  const initials = (prenom || "?").substring(0, 2).toUpperCase();
  const bg = role === "owner"
    ? `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}99 100%)`
    : "linear-gradient(135deg, #1E3A5F 0%, #2563EB 100%)";
  return (
    <div
      data-testid="vendeur-avatar"
      style={{
        width: size, height: size, borderRadius: 8,
        background: bg, display: "flex", alignItems: "center",
        justifyContent: "center", fontSize: size * 0.38,
        fontWeight: 700, color: "#fff", flexShrink: 0,
        letterSpacing: 0.5
      }}
    >
      {initials}
    </div>
  );
}

// [LIVE-VENTES] Ligne de vente dans le feed
function SaleFeedItem({ sale, devise, accentColor, isNew }) {
  const vendeur = sale.vendeur || {};
  const prenom = vendeur.prenom || "Vendeur";
  const role = vendeur.role || "vendeur";
  const mainItem = sale.items?.[0]?.product_name || "Article";
  const extra = (sale.items?.length || 1) - 1;
  const total = (sale.total || 0).toLocaleString("fr-FR");
  const time = sale.created_at
    ? new Date(sale.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
    : "";

  return (
    <motion.div
      data-testid="sale-feed-item"
      initial={isNew ? { opacity: 0, y: -20 } : false}
      animate={{ opacity: 1, y: 0, backgroundColor: isNew ? "rgba(34,197,94,0.08)" : "transparent" }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "10px 0", borderBottom: "1px solid #2A2A2A", gap: 12
      }}
      onAnimationComplete={(def) => {
        // Flash vert disparaît
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
        <VendeurAvatar prenom={prenom} role={role} size={32} accentColor={accentColor} />
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 4, flexWrap: "wrap" }}>
            <span style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>{prenom}</span>
            <span style={{ color: "#9CA3AF", fontSize: 13 }}>a vendu</span>
            <span style={{ color: "#fff", fontSize: 13 }}>{mainItem}</span>
            {extra > 0 && <span style={{ color: "#6B7280", fontSize: 12 }}>+{extra}</span>}
          </div>
        </div>
      </div>
      <div style={{ textAlign: "right", flexShrink: 0 }}>
        <div style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>{total} {devise}</div>
        <div style={{ color: "#6B7280", fontSize: 12 }}>{time}</div>
      </div>
    </motion.div>
  );
}

// [LIVE-VENTES] Barre performance vendeur
function VendeurPerformanceBar({ vendeur, maxCA, devise, accentColor, onClick }) {
  const pct = maxCA > 0 ? (vendeur.total_ca / maxCA) * 100 : 0;
  return (
    <div
      data-testid="vendeur-performance-row"
      onClick={() => onClick?.(vendeur)}
      style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "8px 0", cursor: "pointer"
      }}
    >
      <VendeurAvatar prenom={vendeur.prenom} role={vendeur.role} size={28} accentColor={accentColor} />
      <span style={{ color: "#fff", fontWeight: 700, fontSize: 13, minWidth: 60 }}>
        {vendeur.prenom}
      </span>
      <div style={{ flex: 1, height: 4, borderRadius: 4, background: "#2A2A2A", overflow: "hidden" }}>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          style={{ height: "100%", borderRadius: 4, background: accentColor }}
        />
      </div>
      <span style={{ color: "#fff", fontWeight: 700, fontSize: 13, minWidth: 80, textAlign: "right" }}>
        {vendeur.total_ca.toLocaleString("fr-FR")} {devise}
      </span>
      <span style={{ color: "#9CA3AF", fontSize: 12, minWidth: 55, textAlign: "right" }}>
        {vendeur.nb_ventes} vente{vendeur.nb_ventes > 1 ? "s" : ""}
      </span>
    </div>
  );
}

// [LIVE-VENTES] Composant principal
export default function LiveSalesBlock({ onNewSaleCount, onVendeurClick }) {
  const { getAuthHeaders } = useAuth();
  const [ventes, setVentes] = useState([]);
  const [vendeurs, setVendeurs] = useState([]);
  const [totalVentes, setTotalVentes] = useState(0);
  const [totalCA, setTotalCA] = useState(0);
  const [newIds, setNewIds] = useState(new Set());
  const lastCheckRef = useRef(null);
  const initialLoadDone = useRef(false);

  const boutiqueData = JSON.parse(localStorage.getItem("boutique") || "{}");
  const devise = boutiqueData.currencySymbol || "€";
  const accentColor = boutiqueData.customColor || boutiqueData.colorPalette?.[0] || "#D4A853";

  // [LIVE-VENTES] Chargement initial des ventes du jour
  const fetchToday = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/live-sales/today`, getAuthHeaders());
      setVentes(res.data.ventes || []);
      setVendeurs(res.data.vendeurs || []);
      setTotalVentes(res.data.total_ventes || 0);
      setTotalCA(res.data.total_ca || 0);
      lastCheckRef.current = new Date().toISOString();
      initialLoadDone.current = true;
    } catch {
      // silently fail
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchToday(); }, [fetchToday]);

  // [LIVE-VENTES] Polling toutes les 5 secondes
  useEffect(() => {
    const interval = setInterval(async () => {
      if (!lastCheckRef.current || !initialLoadDone.current) return;
      try {
        const res = await axios.get(
          `${API}/live-sales?since=${encodeURIComponent(lastCheckRef.current)}`,
          getAuthHeaders()
        );
        const nouvelles = res.data.nouvelles || [];
        if (nouvelles.length > 0) {
          // Add new sales to the feed
          const newIdSet = new Set(nouvelles.map(s => s.id));
          setNewIds(newIdSet);
          setVentes(prev => {
            const merged = [...nouvelles, ...prev];
            const seen = new Set();
            return merged.filter(s => {
              if (seen.has(s.id)) return false;
              seen.add(s.id);
              return true;
            }).slice(0, 10);
          });
          lastCheckRef.current = new Date().toISOString();

          // Notify parent (badge count, toast)
          onNewSaleCount?.(nouvelles.length);

          // Update totals
          setTotalVentes(prev => prev + nouvelles.length);
          setTotalCA(prev => prev + nouvelles.reduce((s, v) => s + (v.total || 0), 0));

          // Update vendor stats
          setVendeurs(prev => {
            const map = {};
            prev.forEach(v => { map[v.id] = { ...v }; });
            nouvelles.forEach(inv => {
              const vi = inv.vendeur || {};
              const vid = vi.id || "unknown";
              if (!map[vid]) {
                map[vid] = { id: vid, prenom: vi.prenom || "Inconnu", role: vi.role || "vendeur", total_ca: 0, nb_ventes: 0 };
              }
              map[vid].total_ca += inv.total || 0;
              map[vid].nb_ventes += 1;
            });
            return Object.values(map);
          });

          // Toast for each new sale
          nouvelles.forEach(sale => {
            const v = sale.vendeur || {};
            const item = sale.items?.[0]?.product_name || "Article";
            const total = (sale.total || 0).toLocaleString("fr-FR");
            const time = new Date(sale.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
            toast.custom(() => (
              <div
                data-testid="live-sale-toast"
                style={{
                  background: "#064E3B", border: "1px solid rgba(34,197,94,0.3)",
                  borderRadius: 12, padding: "12px 16px", maxWidth: 300,
                  animation: "slideInRight 300ms ease-out"
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <ShoppingBag size={16} color="#34D399" />
                  <span style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>
                    {v.prenom || "Vendeur"} — {item}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                  <span style={{ color: "#34D399", fontSize: 13 }}>{total} {devise}</span>
                  <span style={{ color: "#9CA3AF", fontSize: 12 }}>{time}</span>
                </div>
              </div>
            ), { duration: 4000, position: "top-right" });
          });

          // Clear new highlight after 2s
          setTimeout(() => setNewIds(new Set()), 2000);
        }
      } catch {
        // silently fail
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [getAuthHeaders, onNewSaleCount, devise]);

  const maxCA = vendeurs.reduce((m, v) => Math.max(m, v.total_ca), 0);

  return (
    <div
      data-testid="live-sales-block"
      style={{
        background: "#1A1A1A", borderRadius: 16, padding: "20px 24px",
        marginBottom: 24, borderLeft: "3px solid #22C55E"
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            data-testid="live-pulse-dot"
            style={{
              width: 8, height: 8, borderRadius: "50%", background: "#22C55E",
              animation: "livePulse 2s infinite", flexShrink: 0
            }}
          />
          <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>Activit&eacute; en direct</span>
          <span style={{ color: "#9CA3AF", fontSize: 13 }}>Aujourd'hui</span>
        </div>
        <div style={{ color: accentColor, fontWeight: 700, fontSize: 14 }}>
          {totalVentes} vente{totalVentes !== 1 ? "s" : ""} — {totalCA.toLocaleString("fr-FR")} {devise}
        </div>
      </div>

      {/* Feed */}
      {ventes.length === 0 ? (
        <div data-testid="no-sales-today" style={{ color: "#6B7280", fontSize: 14, textAlign: "center", padding: "24px 0" }}>
          Aucune vente aujourd'hui. La journ&eacute;e commence !
        </div>
      ) : (
        <div>
          <AnimatePresence>
            {ventes.map(sale => (
              <SaleFeedItem
                key={sale.id}
                sale={sale}
                devise={devise}
                accentColor={accentColor}
                isNew={newIds.has(sale.id)}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Performance équipe */}
      {vendeurs.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ borderTop: "1px solid #2A2A2A", paddingTop: 16, marginBottom: 12 }}>
            <span style={{
              color: "#9CA3AF", fontSize: 13, textTransform: "uppercase",
              letterSpacing: 0.5, fontWeight: 600
            }}>
              Performance de l'&eacute;quipe
            </span>
          </div>
          {vendeurs
            .sort((a, b) => b.total_ca - a.total_ca)
            .map(v => (
              <VendeurPerformanceBar
                key={v.id}
                vendeur={v}
                maxCA={maxCA}
                devise={devise}
                accentColor={accentColor}
                onClick={onVendeurClick}
              />
            ))
          }
        </div>
      )}

      {/* CSS animations */}
      <style>{`
        @keyframes livePulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.3); }
        }
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
